import React from 'react';

import {
    SelectTextField,
    SingleSelectTextFieldProps,
    MultipleSelectTextFieldProps,
} from '@lumapps/combobox/components/SelectTextField';
import { useTranslate } from '@lumapps/translations';
import { ListParameters } from '@lumapps/user-directory/api';
import { useUserDirectories } from '@lumapps/user-directory/hooks/useUserDirectories';
import { UserDirectory } from '@lumapps/user-directory/types';

export type BaseUserDirectoryPickerFieldProps = {
    /**
     * Optional parameters to filter the user directories list.
     */
    params?: ListParameters;
};

type OmittedProps = 'getOptionId' | 'getOptionName' | 'getSectionId' | 'options' | 'autoFilter';

export type UserDirectoryPickerFieldProps = BaseUserDirectoryPickerFieldProps &
    (
        | Omit<SingleSelectTextFieldProps<UserDirectory>, OmittedProps>
        | Omit<MultipleSelectTextFieldProps<UserDirectory>, OmittedProps>
    );

/**
 * A field for selecting one or multiple user directories from the platform.
 * It fetches user directories based on search input and provided parameters.
 */
export const UserDirectoryPickerField: React.FC<UserDirectoryPickerFieldProps> = ({ params, ...props }) => {
    const { translateObject } = useTranslate();
    const [search, setSearch] = React.useState('');
    const [enabled, setEnabled] = React.useState(false);

    const query = useUserDirectories(
        {
            ...params,
            query: search,
        },
        { enabled },
    );

    return (
        <SelectTextField<UserDirectory>
            scope="user-directory-picker-field"
            {...props}
            onChange={(ud: UserDirectory & UserDirectory[]) => {
                // Reset the search when a user directory is selected
                setSearch('');
                // Call the original onChange handler if provided
                props.onChange?.(ud);
            }}
            status={query.baseLoadingStatus}
            autoFilter
            onSearch={setSearch}
            onLoadMore={query.fetchNextPage}
            options={query.items}
            getOptionId="id"
            getOptionName={(ud) => translateObject(ud.title)}
            onOpen={() => {
                setEnabled(true);
            }}
        />
    );
};
